import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { isToggleActive } from '@api/featureToggle';
import {
  findReferralInvite,
  recordReferralInviteEvent,
} from '@api/referralInvite';
import {
  EligibleReferralInvite,
  ReferralInviteEvent,
} from '@interfaces/referralInvite';
import {
  clearReferralInviteCheck,
  hasCheckedReferralInvite,
  markReferralInviteChecked,
} from '@repository/referralInvite';
import useToastContext from '@contexts/ToastContext/hook';
import { buildReferralLink } from '@utils/referralLink';
import { withCrossTabLock } from '@utils/withCrossTabLock';
import {
  ReferralInviteDialog,
  ReferralInviteStep,
} from './ReferralInviteDialog';

export const REFERRAL_SHARE_MESSAGE =
  'Tô usando a VibX pra vender pelo Telegram e facilitar toda a entrega pro fã. Se quiser conhecer, cria sua conta pelo meu link:';

// Só na entrada pelo dashboard: em outra tela, a modal interromperia uma ação.
const ENTRY_PATHNAME = '/';

const CHECK_LOCK = 'referral-invite-check';

const record = (event: ReferralInviteEvent) =>
  recordReferralInviteEvent(event).catch(console.error);

/** Critérios e frequência são da platform-api; aqui se decide só o momento: a entrada. */
export const ReferralInvite = () => {
  const router = useRouter();
  const { data: session } = useSession();
  const toast = useToastContext();
  const userId = session?.user?.id ? Number(session.user.id) : undefined;

  const [invite, setInvite] = useState<EligibleReferralInvite | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<ReferralInviteStep>('offer');

  const hasNavigated = useRef(false);
  useEffect(() => {
    const onRouteChange = () => {
      hasNavigated.current = true;
    };

    router.events.on('routeChangeStart', onRouteChange);
    return () => router.events.off('routeChangeStart', onRouteChange);
  }, [router.events]);

  useEffect(() => {
    if (!userId || hasCheckedReferralInvite(userId)) return;

    markReferralInviteChecked(userId);
    if (router.pathname !== ENTRY_PATHNAME) return;

    let isCancelled = false;
    const isStillAtEntry = () => !isCancelled && !hasNavigated.current;

    const check = async () => {
      // Com o toggle desligado, a rota do convite responderia 403.
      const { success } = await isToggleActive({
        name: 'referralInvite',
        valuesToValidate: { platformUserId: userId },
      });
      if (!success || !isStillAtEntry()) return;

      /*
        Sob lock entre abas, até o "exibido" ser gravado: outra aba aberta junto só consulta depois e já
        recebe a regra semanal. "Exibido" vem depois de abrir, para só contar exibição real.
      */
      await withCrossTabLock(CHECK_LOCK, async () => {
        const found = await findReferralInvite();
        if (!found.eligible || !isStillAtEntry()) return;

        setInvite(found);
        setStep('offer');
        setIsOpen(true);
        await record(ReferralInviteEvent.SHOWN);
      });
    };

    // Falha de rede não gasta a checagem desta aba.
    check().catch((error) => {
      clearReferralInviteCheck(userId);
      console.error(error);
    });

    return () => {
      isCancelled = true;
    };
  }, [userId]);

  if (!invite) return null;

  const link = buildReferralLink(invite.referralCode);

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      toast.setContent(
        'Não foi possível copiar. Selecione o link e copie manualmente.',
        'warning',
      );
      return false;
    }
  };

  const handleCopy = async () => {
    if (!(await copy(link))) return;

    record(ReferralInviteEvent.LINK_COPIED);
    setStep('copied');
  };

  const handleShare = async () => {
    const message = `${REFERRAL_SHARE_MESSAGE}\n${link}`;

    // Sem compartilhamento nativo, copia a mensagem e conta como cópia.
    if (typeof navigator.share !== 'function') {
      if (!(await copy(message))) return;

      record(ReferralInviteEvent.LINK_COPIED);
      setStep('copied');
      return;
    }

    try {
      // Só `text`: com `url`, vários apps repetem o link ou descartam a mensagem.
      await navigator.share({ text: message });
    } catch (error) {
      if ((error as DOMException)?.name === 'AbortError') return;

      if (!(await copy(message))) return;

      record(ReferralInviteEvent.LINK_COPIED);
      setStep('copied');
      return;
    }

    record(ReferralInviteEvent.LINK_SHARED);
    setStep('shared');
  };

  // Só o "Agora não" é recusa; fechar no X não.
  const handleDismiss = () => {
    record(ReferralInviteEvent.DISMISSED);
    setIsOpen(false);
  };

  return (
    <ReferralInviteDialog
      invite={invite}
      step={step}
      link={link}
      isOpen={isOpen}
      onCopy={handleCopy}
      onShare={handleShare}
      onDismiss={handleDismiss}
      onClose={() => setIsOpen(false)}
    />
  );
};
